import React, { ReactNode } from 'react';
import { semantic_colors, Typography } from '../../foundation';
import { BaseText, Stack } from '../../components';
import { CSSValueWithLength } from '../../styles';

export type SectionTitleDirection = 'horizontal' | 'vertical';
export type SectionTitleSize = 'small' | 'medium' | 'large';

export interface SectionTitleProps {
  /** @default horizontal */
  direction?: SectionTitleDirection;
  /** @default large */
  size?: SectionTitleSize;
  caption?: string;
  title: ReactNode;
  description?: ReactNode;
  textButton?: ReactNode;
  actions?: ReactNode;
  mb?: CSSValueWithLength;
}

const text_kind = {
  caption: {
    small: 'Caption_11_Medium',
    medium: 'Body_12_Medium',
    large: 'Body_13_Medium',
  },
  title: {
    small: 'Body_15_Bold',
    medium: 'Heading_18_Bold',
    large: 'Heading_24_Bold',
  },
  description: {
    small: 'Body_12_Regular',
    medium: 'Body_13_Regular',
    large: 'Body_14_Regular',
  },
};

const margin_bottom_size = {
  small: 16,
  medium: 24,
  large: 32,
};

export const SectionTitle = ({
  direction = 'horizontal',
  size = 'large',
  caption,
  title,
  description,
  textButton,
  actions,
  mb,
}: SectionTitleProps) => {
  const default_spacing = size === 'small' ? 4 : 8;
  const default_margin_bottom = mb ?? margin_bottom_size[size];

  return (
    <Stack align='stretch' mb={default_margin_bottom} wrap='wrap' gap={size === 'small' ? 16 : 24}>
      <Stack direction='column' gap={default_spacing}>
        {caption && (
          <BaseText kind={text_kind.caption[size] as Typography} color={semantic_colors.content.secondary}>
            {caption}
          </BaseText>
        )}
        <Stack height='100%' align='center'>
          <BaseText as='h1' kind={text_kind.title[size] as Typography} color={semantic_colors.content.primary}>
            {title}
          </BaseText>
        </Stack>
        {(description || textButton) && (
          <Stack direction='column' gap={default_spacing} align='flex-start'>
            {description && (
              <BaseText kind={text_kind.description[size] as Typography} color={semantic_colors.content.secondary}>
                {description}
              </BaseText>
            )}
            {textButton}
          </Stack>
        )}
      </Stack>
      {actions && (
        <Stack ml='auto' align='flex-end' width={direction === 'vertical' ? '100%' : undefined}>
          {actions}
        </Stack>
      )}
    </Stack>
  );
};
